import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import React, { useEffect, useState } from "react";
import { FaFilePdf } from "react-icons/fa6";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { LuUpload } from "react-icons/lu";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { postApiCallerFormData } from "../../libs/apiCaller";
import { fetchProviderDetails } from "../commonFunctions/CommonFunctions";

const AddProviderDoc = ({
  isOpen,
  onClose,
  setChangeDocumentData,
  changeDocumentData,
  provider,
  fetchProviderProfileDetails,
}) => {
  const [document, setDocument] = useState({
    name: "",
    file: null,
    path: "",
  });
  const dispatch = useDispatch();
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (changeDocumentData && Object.keys(changeDocumentData).length > 0) {
      setDocument({
        name: changeDocumentData.document_name || "",
        file: null,
        path: changeDocumentData.path || "",
      });
    }
  }, [changeDocumentData, provider]);

  useEffect(() => {
    if (!document.file) {
      setPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(document.file);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [document.file]);

  const handleFileChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const fileUrl = URL.createObjectURL(file);
    setDocument((prev) => ({ ...prev, file, path: fileUrl })); // Set blob URL to path
  };

  const removeFile = () => {
    setDocument((prev) => ({ ...prev, file: null, path: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);

    const updatedDocument = {
      ...changeDocumentData,
      document_name: document.name,
      file: document.file,
      path: document.file ? URL.createObjectURL(document.file) : document.path,
    };

    const formData = new FormData();
    formData.append("provider_id", provider?.id);
    formData.append("type", "remove");
    formData.append("document_id", changeDocumentData?.id || "");
    if (document.file) {
      formData.append("document", document.file);
    }

    try {
      const response = await postApiCallerFormData(
        "provider_doc_update",
        formData
      );
      if (response.data?.status) {
        setChangeDocumentData({});
        toast.success("Document updated successfully");
        onClose();
        if (fetchProviderProfileDetails) {
          fetchProviderProfileDetails();
        } else {
          fetchProviderDetails(dispatch, provider?.id);
        }
        setIsUploading(false);
      }
    } catch (error) {
      setIsUploading(false);
      toast.error(
        error?.response?.data?.message || "Failed to update document"
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
            <div className="bg-[#21140E0F] relative px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-semibold text-[#1e3a8a]">
                  Update Document
                </h3>
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Document Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={document.name}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    readOnly
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div className="mb-6">
                    <label className="block text-base font-semibold text-gray-700 mb-2">
                      Upload Document
                    </label>
                    <div className="mt-2 flex justify-center px-6 pt-3 pb-3 border-2 border-dashed border-[#E0A256] rounded-md">
                      <div className="text-center">
                        <LuUpload className="mx-auto text-[#E0A256]" />
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer text-[#E0A256]"
                        >
                          Upload
                        </label>
                        <input
                          id="file-upload"
                          type="file"
                          className="sr-only"
                          onChange={handleFileChange}
                        />
                      </div>
                    </div>
                  </div>
                  {(document.file || document.path) && (
                    <div className="mt-4 relative">
                      <div className="relative rounded-lg border border-gray-200 p-4">
                        <button
                          type="button"
                          onClick={removeFile}
                          className="absolute -right-2 -top-2 bg-red-500 p-1 text-white rounded-full"
                        >
                          <IoIosCloseCircleOutline className="h-4 w-4" />
                        </button>
                        {document.file ? (
                          document.file.type.startsWith("image/") ? (
                            <img
                              src={preview}
                              alt="Preview"
                              className=" object-contain rounded  w-[134px] h-[68px]"
                            />
                          ) : (
                            document.file.type === "application/pdf" && (
                              <div className="flex items-center space-x-2 py-2">
                                <FaFilePdf className="text-red-600 w-[50px] h-[50px]" />
                                {/* PDF Icon */}
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    Updated PDF Document
                                  </p>
                                  <a
                                    href={document.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    View PDF
                                  </a>
                                </div>
                              </div>
                            )
                          )
                        ) : document.path ? (
                          document.path.match(/\.(jpg|jpeg|png)$/i) ? (
                            <img
                              src={document.path}
                              alt="Preview"
                              className="w-[150px] h-[100px] object-contain rounded"
                            />
                          ) : document.path.endsWith(".pdf") ? (
                            <div className="flex items-center space-x-2 py-2">
                              <FaFilePdf className="text-red-600 w-[50px] h-[50px]" />
                              {/* PDF Icon */}
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                  Existing PDF Document
                                </p>
                                <a
                                  href={document.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  View PDF
                                </a>
                              </div>
                            </div>
                          ) : null
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={onClose}
                    className="w-full rounded-xl border border-[#F04438] px-4 py-2 text-sm font-semibold text-[#F04438]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isUploading}
                    className="w-full rounded-xl bg-[#1e3a8a] px-4 py-2 text-sm font-semibold text-white"
                  >
                    {isUploading ? "Updating..." : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default AddProviderDoc;
